'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { COUNTRY_PREFIXES, type CountryPrefix } from '@/lib/phone-prefixes'

export { COUNTRY_PREFIXES }
export type { CountryPrefix }

function flagPath(iso2: string): string {
  return `/flags/nucleo/countries/${iso2.toLowerCase()}.svg`
}

function flagEmoji(iso2: string): string {
  const countryCode = iso2.toUpperCase()
  if (!/^[A-Z]{2}$/.test(countryCode)) return '🏳'
  return countryCode
    .split('')
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join('')
}

type PhonePrefixPickerProps = {
  value: string
  onChange: (code: string) => void
  /** Visual variant — 'dark' suits the contact-lead-form, 'light' suits the empieza-tramite form */
  variant?: 'dark' | 'light'
}

export default function PhonePrefixPicker({
  value,
  onChange,
  variant = 'dark',
}: PhonePrefixPickerProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [selectedCountry, setSelectedCountry] = useState<CountryPrefix | null>(null)
  const [failedFlags, setFailedFlags] = useState<Set<string>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)

  const selected =
    selectedCountry?.code === value
      ? selectedCountry
      : COUNTRY_PREFIXES.find((c) => c.code === value) ?? COUNTRY_PREFIXES[0]
  const filteredPrefixes = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return COUNTRY_PREFIXES
    return COUNTRY_PREFIXES.filter((item) =>
          [item.label, item.code, item.iso2].some((candidate) => candidate.toLowerCase().includes(query)),
        )
  }, [search])

  const renderFlag = (item: CountryPrefix, size: 'trigger' | 'option') => {
    const iso2 = item.iso2.toUpperCase()
    const dimensionsClass = size === 'trigger' ? 'h-4 w-[22px]' : 'h-[15px] w-5'
    const fallbackClass = `${dimensionsClass} flex-shrink-0 rounded-[3px] border border-gray-500/40 bg-gray-800 text-center leading-none`

    if (failedFlags.has(iso2)) {
      return (
        <span className={fallbackClass} aria-hidden="true">
          <span className={size === 'trigger' ? 'text-[13px]' : 'text-xs'}>{flagEmoji(iso2)}</span>
        </span>
      )
    }

    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={flagPath(item.iso2)}
        alt=""
        aria-hidden="true"
        className={`flex-shrink-0 rounded-[3px] border border-gray-500/40 object-cover ${
          dimensionsClass
        }`}
        onError={() => {
          setFailedFlags((current) => {
            if (current.has(iso2)) return current
            const next = new Set(current)
            next.add(iso2)
            return next
          })
        }}
      />
    )
  }

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
        setSearch("")
      }
    }
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        setSearch("")
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('keydown', onEsc)
    }
  }, [])

  const isDark = variant === 'dark'

  const triggerClass = `inline-flex items-center gap-1.5 rounded-xl border px-3 py-3.5 text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${
    isDark
      ? open
        ? 'border-amber-400/85 bg-[#0b1120] text-gray-100 shadow-[0_0_0_1px_rgba(251,191,36,0.35)]'
        : 'border-gray-700 bg-[#0b1120] text-gray-100 hover:border-amber-400/80'
      : open
        ? 'border-amber-500 bg-white text-gray-800 shadow-[0_0_0_1px_rgba(245,158,11,0.35)]'
        : 'border-gray-300 bg-slate-50 text-gray-800 hover:border-amber-500'
  } cursor-pointer`

  const panelClass = `absolute left-0 top-full z-50 mt-1.5 w-[320px] max-h-[380px] overflow-y-auto rounded-xl border shadow-[0_18px_44px_rgba(2,6,23,0.5)] backdrop-blur-xl ${
    isDark
      ? 'border-gray-700/60 bg-gradient-to-b from-gray-900/98 to-gray-950/98'
      : 'border-gray-200 bg-white/98'
  }`

  return (
    <div ref={containerRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => {
          setOpen((o) => !o)
          if (open) setSearch("")
        }}
        className={triggerClass}
        aria-label="Seleccionar prefijo de país"
        aria-expanded={open}
      >
        {renderFlag(selected, 'trigger')}
        <span className="tabular-nums">{selected.code}</span>
        {/* Chevron */}
        <svg
          className={`h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
        >
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className={panelClass} style={{ animation: 'phone-prefix-in 140ms ease-out' }}>
          <div className="sticky top-0 z-10 p-1.5 pb-1">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar país o prefijo..."
              className={`w-full rounded-lg border px-2.5 py-2 text-xs outline-none ${
                isDark
                  ? 'border-gray-700 bg-[#0b1120] text-gray-100 placeholder:text-gray-600 focus:border-amber-400'
                  : 'border-gray-200 bg-white text-gray-800 placeholder:text-gray-400 focus:border-amber-500'
              }`}
              autoComplete="off"
            />
          </div>
          <ul className="p-1.5 pt-0">
            {filteredPrefixes.length === 0 && (
              <li className={`px-2.5 py-3 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Sin resultados
              </li>
            )}
            {filteredPrefixes.map((item) => {
              const isSelected = item.code === value
              return (
                <li key={`${item.iso2}-${item.code}-${item.label}`}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCountry(item)
                      onChange(item.code)
                      setOpen(false)
                      setSearch("")
                    }}
                    className={`flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left text-xs transition-all duration-150 ${
                      isSelected
                        ? isDark
                          ? 'border-amber-400/35 bg-gradient-to-r from-amber-400/15 to-orange-400/8 text-amber-100'
                          : 'border-amber-400/40 bg-amber-50 text-amber-900'
                        : isDark
                          ? 'border-transparent text-gray-200 hover:border-gray-700 hover:bg-gray-800/80 hover:text-gray-100'
                          : 'border-transparent text-gray-700 hover:border-gray-200 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {renderFlag(item, 'option')}
                    <span className="flex-1 font-medium">{item.label}</span>
                    <span className={`tabular-nums font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {item.code}
                    </span>
                    {isSelected && (
                      <svg className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" viewBox="0 0 16 16" aria-hidden="true">
                        <path className="fill-current" d="M6.567 12.24 2.326 8l1.414-1.414 2.827 2.826 5.693-5.693 1.414 1.414-7.107 7.107Z" />
                      </svg>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      <style jsx>{`
        @keyframes phone-prefix-in {
          0% { opacity: 0; transform: translateY(-4px) scale(0.985); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  )
}
